/**
 * ProviderComparisonTable Component
 *
 * Side-by-side comparison of two providers
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ProviderRuleComplianceResponse, RuleComplianceStatsResponse } from '@store/generated/jotaudit';

interface ProviderComparisonTableProps {
  /** Provider breakdown data from comparison mode */
  byProvider: ProviderRuleComplianceResponse[];
  /** All rules data (for reference) */
  rules: RuleComplianceStatsResponse[];
  /** Whether data is loading */
  isLoading?: boolean;
}

interface ProviderSummary {
  userId: string;
  providerName: string;
  totalAudits: number;
  totalCompliant: number;
  totalWarnings: number;
  totalViolations: number;
  overallCompliance: number | null;
  problemRulesCount: number;
  rules: RuleComplianceStatsResponse[];
}

const getComplianceColor = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined) return '#6B7280'; // Gray
  if (rate >= 80) return '#22C55E'; // Green
  if (rate >= 50) return '#EAB308'; // Yellow
  return '#EF4444'; // Red
};

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
};

export const ProviderComparisonTable: React.FC<ProviderComparisonTableProps> = ({
  byProvider,
  rules,
  isLoading = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Compute aggregated stats per provider
  const providerSummaries = useMemo<ProviderSummary[]>(() => {
    return byProvider.map(provider => {
      const providerRules = provider.rules || [];
      const rulesWithData = providerRules.filter(r => (r.total_audits || 0) > 0);
      
      const totalAudits = providerRules.reduce((sum, r) => sum + (r.total_audits || 0), 0);
      const totalCompliant = providerRules.reduce((sum, r) => sum + (r.compliant_count || 0), 0);
      const totalWarnings = providerRules.reduce((sum, r) => sum + (r.warning_count || 0), 0);
      const totalViolations = providerRules.reduce((sum, r) => sum + (r.non_compliant_count || 0), 0);
      const overallCompliance = totalAudits > 0 ? (totalCompliant / totalAudits) * 100 : null;
      const problemRulesCount = rulesWithData.filter(r => (r.compliance_rate || 0) < 80).length;

      return {
        userId: provider.user_id,
        providerName: provider.provider_name || provider.user_id,
        totalAudits,
        totalCompliant,
        totalWarnings,
        totalViolations,
        overallCompliance,
        problemRulesCount,
        rules: rulesWithData.sort((a, b) => {
          // Sort by rule name for consistent comparison
          const aName = a.rule_name || a.rule_id;
          const bName = b.rule_name || b.rule_id;
          return aName.localeCompare(bName);
        }),
      };
    });
  }, [byProvider]);

  // Provider selection state
  const [leftProviderId, setLeftProviderId] = useState<string | null>(null);
  const [rightProviderId, setRightProviderId] = useState<string | null>(null);

  // Auto-select first two providers if available
  useEffect(() => {
    if (providerSummaries.length > 0 && !leftProviderId && !rightProviderId) {
      setLeftProviderId(providerSummaries[0].userId);
      if (providerSummaries.length > 1) {
        setRightProviderId(providerSummaries[1].userId);
      }
    }
  }, [providerSummaries, leftProviderId, rightProviderId]);

  const leftProvider = useMemo(() => 
    providerSummaries.find(p => p.userId === leftProviderId) || null,
    [providerSummaries, leftProviderId]
  );

  const rightProvider = useMemo(() => 
    providerSummaries.find(p => p.userId === rightProviderId) || null,
    [providerSummaries, rightProviderId]
  );

  // Get all unique rules from both providers for comparison
  const comparisonRules = useMemo(() => {
    const ruleMap = new Map<string, RuleComplianceStatsResponse>();
    
    leftProvider?.rules.forEach(rule => {
      ruleMap.set(rule.rule_id, rule);
    });
    
    rightProvider?.rules.forEach(rule => {
      if (!ruleMap.has(rule.rule_id)) {
        ruleMap.set(rule.rule_id, rule);
      }
    });

    return Array.from(ruleMap.values()).sort((a, b) => {
      const aName = a.rule_name || a.rule_id;
      const bName = b.rule_name || b.rule_id;
      return aName.localeCompare(bName);
    });
  }, [leftProvider, rightProvider]);


  const handleSwap = useCallback(() => {
    const temp = leftProviderId;
    setLeftProviderId(rightProviderId);
    setRightProviderId(temp);
  }, [leftProviderId, rightProviderId]);

  const handleRuleClick = useCallback(
    (ruleId: string, userId: string | null) => {
      if (userId) {
        navigate(`/jotaudit/rules/${ruleId}/analytics?user_id=${userId}`);
      }
    },
    [navigate],
  );

  const getRuleForProvider = useCallback(
    (provider: ProviderSummary | null, ruleId: string): RuleComplianceStatsResponse | null => {
      if (!provider) return null;
      return provider.rules.find(r => r.rule_id === ruleId) || null;
    },
    []
  );

  if (isLoading && byProvider.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Provider Comparison
          </Typography>
        </Box>
        <Skeleton variant="rectangular"  sx={{ width: "100%", height: 400 }}/>
      </Paper>
    );
  }

  if (byProvider.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No comparison data available. Enable comparison mode to see provider breakdown.
        </Typography>
      </Paper>
    );
  }

  if (byProvider.length < 2) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Need at least 2 providers to compare. Currently showing {byProvider.length} provider(s).
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header with Provider Selection */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            Provider Comparison
          </Typography>
          <IconButton size="small" onClick={handleSwap} disabled={!leftProviderId || !rightProviderId}>
            <SwapIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Autocomplete
            options={providerSummaries}
            getOptionLabel={(option) => option.providerName}
            value={leftProvider}
            onChange={(_e, value) => setLeftProviderId(value?.userId || null)}
            renderInput={(params) => <TextField {...params} label="Provider 1" size="small" />}
            sx={{ flex: 1 }}
            disableClearable={false}
          />
          <Autocomplete
            options={providerSummaries.filter(p => p.userId !== leftProviderId)}
            getOptionLabel={(option) => option.providerName}
            value={rightProvider}
            onChange={(_e, value) => setRightProviderId(value?.userId || null)}
            renderInput={(params) => <TextField {...params} label="Provider 2" size="small" />}
            sx={{ flex: 1 }}
            disableClearable={false}
          />
        </Box>
      </Box>

      {/* Side-by-Side Comparison */}
      {leftProvider && rightProvider ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Left Column */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRight: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
            }}
          >
            {/* Left Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Provider 1
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                {leftProvider.providerName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Compliance</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getComplianceColor(leftProvider.overallCompliance) }}>
                    {formatPercent(leftProvider.overallCompliance)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Audits</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {leftProvider.totalAudits}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Violations</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: leftProvider.totalViolations > 0 ? 'error.main' : 'inherit' }}>
                    {leftProvider.totalViolations}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Left Content - Scrollable */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                maxHeight: 600,
              }}
            >
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                      <TableCell sx={{ fontWeight: 600 }}>Rule</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Compliance</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>Audits</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>Violations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonRules.map(rule => {
                      const providerRule = getRuleForProvider(leftProvider, rule.rule_id);
                      return (
                        <TableRow
                          key={rule.rule_id}
                          sx={{
                            cursor: providerRule ? 'pointer' : 'default',
                            opacity: providerRule ? 1 : 0.5,
                            '&:hover': providerRule ? { bgcolor: theme.palette.action.hover } : {},
                          }}
                          onClick={() => providerRule && handleRuleClick(rule.rule_id, leftProvider.userId)}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.8125rem',
                                textDecoration: rule.is_deleted ? 'line-through' : 'none',
                                color: rule.is_disabled
                                  ? theme.palette.text.disabled
                                  : rule.is_deleted
                                    ? theme.palette.text.secondary
                                    : 'inherit',
                                opacity: rule.is_disabled ? 0.6 : 1,
                              }}
                            >
                              {rule.rule_name || rule.rule_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {providerRule ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 40 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={providerRule.compliance_rate || 0}
                                    sx={{
                                      height: 6,
                                      borderRadius: 1,
                                      bgcolor: theme.palette.grey[200],
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: getComplianceColor(providerRule.compliance_rate),
                                      },
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    minWidth: 40,
                                    fontWeight: 500,
                                    color: getComplianceColor(providerRule.compliance_rate),
                                  }}
                                >
                                  {formatPercent(providerRule.compliance_rate)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {providerRule?.total_audits || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                color: (providerRule?.non_compliant_count || 0) > 0 ? theme.palette.error.main : 'inherit',
                              }}
                            >
                              {providerRule?.non_compliant_count || 0}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Right Column */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Right Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Provider 2
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                {rightProvider.providerName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Compliance</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: getComplianceColor(rightProvider.overallCompliance) }}>
                    {formatPercent(rightProvider.overallCompliance)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Audits</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {rightProvider.totalAudits}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Violations</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: rightProvider.totalViolations > 0 ? 'error.main' : 'inherit' }}>
                    {rightProvider.totalViolations}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Content - Scrollable */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                maxHeight: 600,
              }}
            >
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                      <TableCell sx={{ fontWeight: 600 }}>Rule</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Compliance</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>Audits</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 80 }}>Violations</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comparisonRules.map(rule => {
                      const providerRule = getRuleForProvider(rightProvider, rule.rule_id);
                      return (
                        <TableRow
                          key={rule.rule_id}
                          sx={{
                            cursor: providerRule ? 'pointer' : 'default',
                            opacity: providerRule ? 1 : 0.5,
                            '&:hover': providerRule ? { bgcolor: theme.palette.action.hover } : {},
                          }}
                          onClick={() => providerRule && handleRuleClick(rule.rule_id, rightProvider.userId)}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.8125rem',
                                textDecoration: rule.is_deleted ? 'line-through' : 'none',
                                color: rule.is_disabled
                                  ? theme.palette.text.disabled
                                  : rule.is_deleted
                                    ? theme.palette.text.secondary
                                    : 'inherit',
                                opacity: rule.is_disabled ? 0.6 : 1,
                              }}
                            >
                              {rule.rule_name || rule.rule_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {providerRule ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, minWidth: 40 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={providerRule.compliance_rate || 0}
                                    sx={{
                                      height: 6,
                                      borderRadius: 1,
                                      bgcolor: theme.palette.grey[200],
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: getComplianceColor(providerRule.compliance_rate),
                                      },
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    minWidth: 40,
                                    fontWeight: 500,
                                    color: getComplianceColor(providerRule.compliance_rate),
                                  }}
                                >
                                  {formatPercent(providerRule.compliance_rate)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {providerRule?.total_audits || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                color: (providerRule?.non_compliant_count || 0) > 0 ? theme.palette.error.main : 'inherit',
                              }}
                            >
                              {providerRule?.non_compliant_count || 0}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select two providers to compare
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ProviderComparisonTable;
